import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../mock';

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="col-span-2 md:col-span-1">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 mb-4" />
            <p className="text-slate-400 text-sm mb-4">
              Calgary's trusted carpet cleaning experts. Professional, affordable, and guaranteed results.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/share/17iS7zmp9J/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-cyan-400 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a 
                href="https://www.instagram.com/carpexcleaning?igsh=ZTUxYW90b3h3Z2Y4&utm_source=ig_contact_invite" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-cyan-400 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-6 w-6" />
              </a>
              <a 
                href="https://www.yelp.ca/biz/carpex-cleaning-services-calgary" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-cyan-400 transition-colors"
                aria-label="Yelp"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.905-4.308a1.072 1.072 0 0 1 1.596-.206 9.194 9.194 0 0 1 2.364 3.252 1.073 1.073 0 0 1-.694 1.459zm-3.397 5.906a1.073 1.073 0 0 1-1.168-.631l-1.883-4.763c-.372-.94.606-1.835 1.476-1.348l4.489 2.506c.585.327.708 1.119.252 1.588a9.201 9.201 0 0 1-3.166 2.648zM9.5 12.095l2.56-4.071c.506-.806 1.69-.543 1.786.397l.498 4.876a1.073 1.073 0 0 1-.886 1.159 9.194 9.194 0 0 1-3.983-.219 1.073 1.073 0 0 1 .025-2.142zm.39 8.226a1.073 1.073 0 0 1-1.28-.793l-.906-3.466c-.253-.97.833-1.727 1.635-1.138l4.017 2.95c.54.397.495 1.203-.089 1.536a9.194 9.194 0 0 1-3.377 1.91zM5.163 5.252a9.194 9.194 0 0 1 3.6-2.53 1.073 1.073 0 0 1 1.453.691l1.132 3.888c.282.97-.746 1.797-1.553 1.247L5.28 5.34a.268.268 0 0 1-.117-.088z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a href="/#home" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Home
                </a>
              </li>
              <li>
                <a href="/#services" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Services
                </a>
              </li>
              <li>
                <a href="/#pricing" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Pricing
                </a>
              </li>
              <li>
                <a href="/#gallery" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Gallery
                </a>
              </li>
              <li>
                <a href="/#contact" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Book Now
                </a>
              </li>
              <li>
                <Link to="/terms" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Our Services</h3>
            <ul className="space-y-2">
              <li className="text-slate-400">Carpet Steam Cleaning</li>
              <li className="text-slate-400">Upholstery Cleaning</li>
              <li className="text-slate-400">Mattress Cleaning</li>
              <li className="text-slate-400">Area Rug Cleaning</li>
              <li className="text-slate-400">Pet Stain Removal</li>
              <li className="text-slate-400">Commercial Cleaning</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center text-slate-400">
                <Phone className="h-5 w-5 mr-3 text-cyan-500" />
                <a href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`} className="hover:text-cyan-400 transition-colors">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center text-slate-400">
                <Mail className="h-5 w-5 mr-3 text-cyan-500" />
                <a href={`mailto:${companyInfo.email}`} className="hover:text-cyan-400 transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-center text-slate-400">
                <Clock className="h-5 w-5 mr-3 text-cyan-500" />
                <span>{companyInfo.hours}</span>
              </li>
              <li className="flex items-start text-slate-400">
                <MapPin className="h-5 w-5 mr-3 text-cyan-500 flex-shrink-0 mt-0.5" />
                <span>Serving Calgary & Surrounding Areas</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} {companyInfo.name}. All rights reserved.</p>
          <p className="mt-2">Professional Carpet Cleaning Services in Calgary, Alberta</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
